import React, { useState, useEffect, useMemo } from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS, RADIUS } from "../../../theme/materials";
import { templateApi } from "../../../api/client";
import { Button, TextArea, TogglePill } from "../../../components/ui";
import { useAuth } from "../../../contexts/AuthContext";
import IconTile from "../../../components/IconTile";
import Icon from "../../../components/Icon";
import { resolveTemplateIcon, categoryLabel } from "../iphone-grid/iconForTemplate";

// i2-visual-identity-v2.md §10.1 A3 — MarketplaceViewer.
//
// Unifies the legacy <TemplateDetailPanel> (read-only details) and
// <InstallTemplateModal> (configure + install form) into ONE
// surface mirroring AgentViewer's contract: drawer → tabs → footer.
//
// Three tabs:
//   - Overview  — description, categories, capabilities, snapshot
//   - Configure — name + channels (lifted from InstallTemplateModal)
//   - Reviews   — ratings list + submit-your-rating widget
//
// Footer carries the always-visible Install button (reads name +
// channels from Configure-tab state). Esc + backdrop click close.
//
// Props:
//   template      — the template object (required; null = render nothing)
//   onClose       — () => void
//   onInstalled   — () => void  (fires after successful install)
//   onDelete      — (templateId) => void  (fires after successful delete)
//   initialTab    — "overview" | "configure" | "reviews" (default "overview";
//                   deep-link ?install=<slug> passes "configure")
//
// Component contract: Notes/marketplace-viewer-contract.md.

const fmtVer = (v) => v ? `v${String(v).replace(/^v+/, "")}` : "";

const ALL_CHANNELS = [
  { type: "chat",     label: "Webchat"  },
  { type: "whatsapp", label: "WhatsApp" },
  { type: "voice",    label: "Voice"    },
  { type: "sms",      label: "SMS"      },
  { type: "email",    label: "Email"    },
  { type: "webhook",  label: "Webhook"  },
];

const TABS = [
  { id: "overview",  label: "Overview"  },
  { id: "configure", label: "Configure" },
  { id: "reviews",   label: "Reviews"   },
];

const MarketplaceViewer = ({
  template,
  onClose,
  onInstalled,
  onDelete,
  initialTab = "overview",
}) => {
  const { currentUser, currentTenant } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);

  // Configure tab state (lifted from InstallTemplateModal)
  const [name, setName] = useState(template?.name || "");
  const [channels, setChannels] = useState(["chat"]);
  const [installing, setInstalling] = useState(false);
  const [installError, setInstallError] = useState(null);

  // Reviews tab state (lifted from TemplateDetailPanel)
  const [ratings, setRatings] = useState([]);
  const [userRating, setUserRating] = useState(0);
  const [userReview, setUserReview] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);

  // Delete state (owner-only)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Reset form state when template changes (e.g. user closes + reopens
  // on a different template in the same browse session).
  useEffect(() => {
    if (template) {
      setName(template.name || "");
      setChannels(["chat"]);
      setInstallError(null);
      setUserRating(0);
      setUserReview("");
      setConfirmDelete(false);
      setDeleteError(null);
      setActiveTab(initialTab);
    }
  }, [template?.id, initialTab]);

  // Lazy-load ratings only when Reviews tab is selected.
  useEffect(() => {
    if (template?.id && activeTab === "reviews" && ratings.length === 0) {
      templateApi.ratings(template.id).then((res) => {
        if (res.success) setRatings(res.data?.ratings || []);
      }).catch(() => {});
    }
  }, [template?.id, activeTab]);

  // Esc closes the viewer.
  useEffect(() => {
    if (!template) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [template, onClose]);

  if (!template) return null;

  const snapshot = template.agent_snapshot || template.tree_snapshot || {};
  const { category: tileCategory, iconName } = resolveTemplateIcon(template);

  const canDelete = ["owner", "administrator", "developer"].includes(currentUser?.role)
    && template.tenant_id === currentTenant?.id;

  const toggleChannel = (ch) => {
    setChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]
    );
  };

  const handleInstall = async () => {
    if (!name.trim()) {
      setInstallError("Agent name is required");
      return;
    }
    if (channels.length === 0) {
      setInstallError("At least one channel is required");
      return;
    }
    setInstalling(true);
    setInstallError(null);
    try {
      const res = await templateApi.install(template.id, {
        name: name.trim(), channels,
      });
      if (res.success) {
        onInstalled && onInstalled();
        onClose && onClose();
      } else {
        setInstallError(res.error || "Install failed");
      }
    } catch (e) {
      setInstallError(String(e?.message || e || "Install failed"));
    } finally {
      setInstalling(false);
    }
  };

  const handleRate = async () => {
    if (!userRating) return;
    setSubmittingRating(true);
    try {
      await templateApi.rate(template.id, {
        rating: userRating, review: userReview || undefined,
      });
      const res = await templateApi.ratings(template.id);
      if (res.success) setRatings(res.data?.ratings || []);
      setUserReview("");
      setUserRating(0);
    } catch {} finally {
      setSubmittingRating(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await templateApi.delete(template.id);
      onDelete && onDelete(template.id);
      onClose && onClose();
    } catch (e) {
      setDeleteError(String(e?.message || e || "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  const installDisabled = installing || !name.trim() || channels.length === 0;

  return (
    <>
      {/* Backdrop is a sibling overlay (not parent of viewer) so the
          click handler is unambiguously on the backdrop, never absorbed
          by the panel. Sibling layering controlled by zIndex.

          top: 48px / height: calc(100% - 48px) accounts for Shell's
          topbar (height 48). Shell's main-content has position:relative
          + zIndex:1 which creates a stacking context — fixed children
          here can't escape the main-content's zIndex:1 in Shell's root
          stack, so the topbar (Shell sibling, zIndex:10) paints over
          the top 48px of any drawer rendered inside main-content.
          Offsetting the drawer past the topbar restores the visible
          IconTile + title without needing to restructure Shell's
          stacking contexts. */}
      <div
        data-testid="marketplace-viewer-backdrop"
        onClick={onClose}
        style={{
          position: "fixed", top: 48, left: 0, right: 0, bottom: 0, zIndex: 1500,
          background: "rgba(0,0,0,0.55)",
        }}
      />
      <div
        data-testid="marketplace-viewer"
        className="panel-scroll"
        role="dialog"
        aria-modal="true"
        style={{
          ...MATERIALS.thickGlass(),
          position: "fixed", top: 48, right: 0, height: "calc(100% - 48px)",
          width: 480, maxWidth: "92vw", zIndex: 1501,
          display: "flex", flexDirection: "column",
          borderLeft: `1px solid ${COLORS.border}`,
          boxShadow: MATERIALS.spatialShadow ? MATERIALS.spatialShadow() : "-12px 0 40px rgba(0,0,0,0.45)",
          animation: "slideInRight 0.22s ease-out",
        }}
      >
        {/* ── Header — IconTile + name + author + version ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "20px 24px 16px",
          borderBottom: `1px solid ${COLORS.border}`,
          position: "relative",
        }}>
          <IconTile category={tileCategory} iconName={iconName} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 18, fontWeight: 700, color: COLORS.text, lineHeight: 1.2,
              fontFamily: "Outfit, sans-serif",
            }}>{template.name}</div>
            <div style={{
              fontSize: 11, color: COLORS.textDim, marginTop: 4,
              display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
            }}>
              <span>by {template.author_name || "Unknown"}</span>
              {fmtVer(template.version || template.schema_version) && (
                <>
                  <span>·</span>
                  <span style={{ fontFamily: "monospace" }}>{fmtVer(template.version || template.schema_version)}</span>
                </>
              )}
              <span>·</span>
              <span style={{ color: COLORS.cyan }}>{categoryLabel(template.category)}</span>
              {template.avg_rating ? (
                <>
                  <span>·</span>
                  <span style={{ color: COLORS.orange }}>★ {Number(template.avg_rating).toFixed(1)}</span>
                </>
              ) : null}
              {template.install_count ? (
                <>
                  <span>·</span>
                  <span>{template.install_count} installs</span>
                </>
              ) : null}
            </div>
          </div>
          <button
            onClick={onClose}
            title="Close (Esc)"
            style={{
              width: 32, height: 32, borderRadius: RADIUS.deep,
              background: "transparent", border: `1px solid ${COLORS.border}`,
              color: COLORS.textDim, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Icon name="action.close" size={14} color={COLORS.textDim} />
          </button>
        </div>

        {/* ── Tabs ── */}
        <div style={{
          display: "flex", gap: 4, padding: "12px 20px 0",
          borderBottom: `1px solid ${COLORS.border}`,
          background: "transparent",
        }}>
          {TABS.map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`marketplace-viewer-tab-${t.id}`}
                role="tab"
                aria-selected={isActive ? "true" : "false"}
                onClick={() => setActiveTab(t.id)}
                style={{
                  padding: "8px 14px",
                  fontSize: 12, fontWeight: isActive ? 700 : 500,
                  background: "transparent", border: "none",
                  borderBottom: `2px solid ${isActive ? COLORS.accent : "transparent"}`,
                  color: isActive ? COLORS.text : COLORS.textDim,
                  cursor: "pointer", letterSpacing: "0.02em",
                  fontFamily: "inherit",
                }}
              >{t.label}</button>
            );
          })}
        </div>

        {/* ── Tab content (scrollable) ── */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "20px 24px",
        }}>
          {activeTab === "overview" && (
            <OverviewTabContent template={template} snapshot={snapshot} />
          )}
          {activeTab === "configure" && (
            <ConfigureTabContent
              template={template}
              name={name} setName={setName}
              channels={channels} toggleChannel={toggleChannel}
              error={installError}
            />
          )}
          {activeTab === "reviews" && (
            <ReviewsTabContent
              ratings={ratings}
              userRating={userRating} setUserRating={setUserRating}
              userReview={userReview} setUserReview={setUserReview}
              submittingRating={submittingRating}
              onSubmit={handleRate}
            />
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, padding: "14px 24px",
          borderTop: `1px solid ${COLORS.border}`,
          background: "transparent",
        }}>
          {/* Delete (owner-only, left side) */}
          <div>
            {canDelete && !confirmDelete && (
              <button
                onClick={() => setConfirmDelete(true)}
                title="Delete template"
                style={{
                  width: 32, height: 32, borderRadius: RADIUS.deep,
                  background: "transparent",
                  border: `1px solid ${COLORS.pink || "#ef4444"}40`,
                  color: COLORS.pink || "#ef4444", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              ><Icon name="action.delete" size={14} /></button>
            )}
            {canDelete && confirmDelete && (
              <div style={{ display: "flex", gap: 6 }}>
                <Button
                  variant="primary"
                  onClick={handleDelete}
                  disabled={deleting}
                  loading={deleting}
                  style={{
                    background: COLORS.pink || "#ef4444",
                    padding: "6px 12px", fontSize: 11,
                  }}
                >{deleting ? "Deleting…" : "Confirm delete"}</Button>
                <Button
                  variant="secondary"
                  onClick={() => setConfirmDelete(false)}
                  style={{ padding: "6px 12px", fontSize: 11 }}
                >Cancel</Button>
              </div>
            )}
            {deleteError && (
              <div style={{
                fontSize: 10, color: COLORS.pink || "#ef4444",
                marginTop: 4,
              }}>{deleteError}</div>
            )}
          </div>

          {/* Cancel + Install (right side) */}
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={installing}
              style={{ padding: "10px 16px", fontSize: 12 }}
            >Cancel</Button>
            <Button
              variant="primary"
              onClick={handleInstall}
              disabled={installDisabled}
              loading={installing}
              data-testid="marketplace-viewer-install-btn"
              style={{
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                padding: "10px 18px", fontSize: 13, fontWeight: 700,
                letterSpacing: "0.02em",
                opacity: installDisabled ? 0.5 : 1,
              }}
            >{installing ? "Installing…" : "Install"}</Button>
          </div>
        </div>
      </div>
    </>
  );
};


// ──────────────────────────────────────────────────────────────────
// Tab content components (lazy-rendered by parent)
// ──────────────────────────────────────────────────────────────────


function OverviewTabContent({ template, snapshot }) {
  const stats = [
    { label: "Installs", value: template.install_count || 0, color: COLORS.green },
    {
      label: "Rating",
      value: template.avg_rating ? `${Number(template.avg_rating).toFixed(1)} ★` : "—",
      color: COLORS.orange,
    },
    {
      label: "Visibility",
      value: template.visibility === "global" ? "Global"
            : template.visibility === "public" ? "Public" : "Private",
      color: COLORS.cyan,
    },
  ];
  const [showPrompt, setShowPrompt] = useState(false);
  return (
    <>
      {/* Stats row */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {stats.map((s) => (
          <div
            key={s.label}
            style={{
              flex: 1, padding: "10px 12px", borderRadius: RADIUS.inner,
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: s.color, fontFamily: "Outfit, sans-serif" }}>
              {s.value}
            </div>
            <div style={{
              fontSize: 10, color: COLORS.textDim, marginTop: 2,
              textTransform: "uppercase", letterSpacing: "0.04em",
            }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Description */}
      <Section title="Description">
        <div style={{
          fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6,
          padding: "12px 14px", borderRadius: RADIUS.deep,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        }}>
          {template.description || (
            <span style={{ fontStyle: "italic" }}>No description provided.</span>
          )}
        </div>
      </Section>

      {/* Tags */}
      {template.tags?.length > 0 && (
        <Section title="Tags">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {template.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  padding: "3px 10px", borderRadius: RADIUS.pill, fontSize: 10,
                  background: COLORS.surface, color: COLORS.textMuted,
                  border: `1px solid ${COLORS.border}`,
                }}
              >{tag}</span>
            ))}
          </div>
        </Section>
      )}

      {/* Agent configuration */}
      <Section title="Agent configuration">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <ConfigItem label="Model"       value={snapshot.model || "—"} />
          <ConfigItem label="Provider"    value={snapshot.provider || "—"} />
          <ConfigItem label="Temperature" value={snapshot.temperature ?? "—"} />
          <ConfigItem label="Max tokens"  value={snapshot.max_tokens || "—"} />
        </div>
        {snapshot.system_prompt && (
          <div style={{ marginTop: 10 }}>
            <button
              onClick={() => setShowPrompt(!showPrompt)}
              style={{
                background: "transparent", border: "none",
                color: COLORS.accentText, padding: 0,
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}
            >{showPrompt ? "Hide" : "Show"} system prompt</button>
            {showPrompt && (
              <pre style={{
                margin: "8px 0 0", padding: "10px 12px", borderRadius: RADIUS.deep,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                fontSize: 11, color: COLORS.text, lineHeight: 1.5,
                fontFamily: "monospace", whiteSpace: "pre-wrap",
                maxHeight: 240, overflow: "auto",
              }}>{snapshot.system_prompt}</pre>
            )}
          </div>
        )}
      </Section>
    </>
  );
}


function ConfigureTabContent({ template, name, setName, channels, toggleChannel, error }) {
  return (
    <>
      <Section title="Agent name">
        <input
          type="text"
          data-testid="marketplace-viewer-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={template.name}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "10px 12px", fontSize: 13, color: COLORS.text,
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner,
            outline: "none", fontFamily: "inherit",
          }}
        />
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
          You can rename later in Detail Viewer → Overview.
        </div>
      </Section>

      <Section title="Channels">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {ALL_CHANNELS.map((c) => (
            <TogglePill
              key={c.type}
              data-testid={`marketplace-viewer-channel-${c.type}`}
              active={channels.includes(c.type)}
              onClick={() => toggleChannel(c.type)}
              size="sm"
            >{c.label}</TogglePill>
          ))}
        </div>
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 6 }}>
          At least one channel required. Add more later in Detail Viewer → Wiring.
        </div>
      </Section>

      {error && (
        <div
          style={{
            padding: "10px 12px", borderRadius: RADIUS.deep,
            background: `${COLORS.pink || "#ef4444"}15`,
            border: `1px solid ${COLORS.pink || "#ef4444"}55`,
            color: COLORS.pink || "#ef4444", fontSize: 12,
            marginBottom: 16,
          }}
        >{error}</div>
      )}
    </>
  );
}


function ReviewsTabContent({
  ratings, userRating, setUserRating, userReview, setUserReview,
  submittingRating, onSubmit,
}) {
  return (
    <div data-testid="marketplace-viewer-reviews-panel">
      <Section title="Leave your review">
        <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              data-testid={`marketplace-viewer-reviews-star-${star}`}
              onClick={() => setUserRating(star)}
              style={{
                fontSize: 22, background: "transparent", border: "none",
                cursor: "pointer", padding: "0 2px",
                color: star <= userRating ? COLORS.orange : COLORS.textDim,
                transition: "color 0.1s",
              }}
            >★</button>
          ))}
        </div>
        <TextArea
          value={userReview}
          onChange={(e) => setUserReview(e.target.value)}
          rows={2}
          placeholder="Write a review (optional)"
          style={{ marginBottom: 8 }}
        />
        <Button
          data-testid="marketplace-viewer-reviews-submit"
          onClick={onSubmit}
          disabled={!userRating}
          loading={submittingRating}
        >Submit rating</Button>
      </Section>

      <Section title={ratings.length > 0 ? `Reviews (${ratings.length})` : "No reviews yet"}>
        {ratings.length > 0 ? (
          <div
            data-testid="marketplace-viewer-reviews-list"
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {ratings.map((r) => (
              <div
                key={r.id}
                data-testid={`rating-row-${r.id}`}
                style={{
                  padding: "10px 12px", borderRadius: RADIUS.deep,
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                }}
              >
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginBottom: 4,
                }}>
                  <span style={{ fontSize: 12, color: COLORS.orange }}>
                    {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                  </span>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                  </span>
                </div>
                {r.review && (
                  <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.4 }}>
                    {r.review}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            fontSize: 12, color: COLORS.textDim, fontStyle: "italic",
            padding: "10px 0",
          }}>Be the first to rate this template.</div>
        )}
      </Section>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: COLORS.textDim,
        textTransform: "uppercase", letterSpacing: "0.08em",
        marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  );
}


function ConfigItem({ label, value }) {
  return (
    <div style={{
      padding: "8px 10px", borderRadius: RADIUS.deep,
      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
    }}>
      <div style={{
        fontSize: 10, color: COLORS.textDim, marginBottom: 2,
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}>{label}</div>
      <div style={{
        fontSize: 12, color: COLORS.text, fontWeight: 500,
        fontFamily: "monospace",
      }}>{value}</div>
    </div>
  );
}


export default MarketplaceViewer;
